import React, { useEffect, useRef } from 'react';
import { Mail, Send, X, Loader2, AlertTriangle, CheckCircle2, Users, Shield } from 'lucide-react';

export const SENDER_PERSONAS = [
  { key: 'hello', label: 'Mina (hello@) — friendly default' },
  { key: 'intro', label: 'Nina (intro@) — onboarding tone' },
  { key: 'support', label: 'Zara (support@) — customer support tone' },
  { key: 'admin', label: 'Adam (admin@) — operational/admin tone' },
  { key: 'ceo', label: 'Ade (ceo@) — formal announcements' },
];

const MERGE_TAGS = [
  { tag: '{{first_name}}', label: 'First name', sample: 'Sade' },
  { tag: '{{full_name}}', label: 'Full name', sample: 'Sade Adeyemi' },
  { tag: '{{country}}', label: 'Country', sample: 'Nigeria' },
];

// Active filter chips for display in the preview modal
function FilterChips({ filters }) {
  const entries = Object.entries(filters || {}).filter(([k, v]) => {
    if (v === null || v === undefined || v === '') return false;
    if (k === 'status' && v === 'active') return false; // default
    return true;
  });
  if (entries.length === 0) {
    return <span className="text-xs text-gray-500 italic">No filters active — this will email every active user</span>;
  }
  const labelize = (k, v) => {
    if (k === 'is_beta_tester') return v ? 'Beta testers only' : 'Non-beta only';
    if (k === 'auth_type') return `Auth: ${v}`;
    if (k === 'country') return `Country: ${v}`;
    if (k === 'role') return `Role: ${v}`;
    if (k === 'roles') return `Roles: ${v}`;
    if (k === 'status') return `Status: ${v}`;
    if (k === 'created_after') return `Created after: ${new Date(v).toLocaleDateString()}`;
    if (k === 'account_age_bucket') return `Age: ${v.replace(/_/g, '–')}`;
    if (k === 'last_login_within_days') return `Login within ${v} days`;
    if (k === 'activity_status') return `Activity: ${v}`;
    return `${k}: ${v}`;
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="campaign-filter-chips">
      {entries.map(([k, v]) => (
        <span key={k} className="px-2 py-0.5 rounded-full text-[11px] bg-green-500/10 text-green-300 border border-green-500/30">
          {labelize(k, v)}
        </span>
      ))}
    </div>
  );
}

function QuotaBanner({ quota, audienceCount }) {
  if (!quota) return null;
  const dailyRemaining = quota.daily_cap - quota.daily_used;
  const monthlyRemaining = quota.monthly_cap - quota.monthly_used;
  const willOverflowMonth = audienceCount > monthlyRemaining;
  const willTakeDays = audienceCount > dailyRemaining && audienceCount <= monthlyRemaining;

  if (willOverflowMonth) {
    return (
      <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs flex items-start gap-2" data-testid="campaign-quota-banner-overflow">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold mb-1">Monthly quota will not cover this audience</div>
          You've used {quota.monthly_used.toLocaleString()} of {quota.monthly_cap.toLocaleString()} emails this month.
          Only {monthlyRemaining.toLocaleString()} will send. Consider upgrading Resend or reducing audience.
        </div>
      </div>
    );
  }
  if (willTakeDays) {
    const days = Math.ceil(audienceCount / quota.daily_cap);
    return (
      <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs flex items-start gap-2" data-testid="campaign-quota-banner-days">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          Sending {audienceCount.toLocaleString()} emails will take ~{days} days at the current daily cap ({quota.daily_cap}/day).
        </div>
      </div>
    );
  }
  return null;
}

// ─────────────────────────────────────────────
// Step 1: Audience Preview
// ─────────────────────────────────────────────
export function PreviewStep({ preview, loading, error, onCancel, onContinue, senderPersona, setSenderPersona }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="campaign-preview-loading">
        <Loader2 className="w-6 h-6 animate-spin text-green-400" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm" data-testid="campaign-preview-error">
        {error}
      </div>
    );
  }
  if (!preview) return null;

  const { audience_count, suppressed_count, sample_recipients, filters_snapshot, quota, recently_emailed_count, cooldown_days } = preview;
  const isEmpty = audience_count === 0;

  return (
    <div className="space-y-4" data-testid="campaign-preview-step">
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 rounded-lg bg-gray-900/60 border border-gray-700">
          <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Will email</div>
          <div className="text-2xl font-bold text-green-400" data-testid="campaign-audience-count">
            {audience_count.toLocaleString()} {audience_count === 1 ? 'user' : 'users'}
          </div>
        </div>
        <div className="p-3 rounded-lg bg-gray-900/60 border border-gray-700">
          <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Suppressed</div>
          <div className="text-2xl font-bold text-gray-400" data-testid="campaign-suppressed-count">
            {suppressed_count.toLocaleString()}
          </div>
          <div className="text-[10px] text-gray-500 mt-0.5">Previously unsubscribed</div>
        </div>
      </div>

      <QuotaBanner quota={quota} audienceCount={audience_count} />

      {recently_emailed_count > 0 && (
        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs flex items-start gap-2" data-testid="campaign-cooldown-banner">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold mb-0.5">Recently contacted</div>
            <span className="text-amber-200">{recently_emailed_count.toLocaleString()} of these users</span> received a follow-up in the last <strong>{cooldown_days} days</strong>. They'll still receive this one — proceed only if it's substantively different.
          </div>
        </div>
      )}

      <div>
        <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Filters applied</div>
        <FilterChips filters={filters_snapshot} />
      </div>

      {sample_recipients && sample_recipients.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-400 mb-2 flex items-center gap-1">
            <Users className="w-3 h-3" /> Sample recipients (first {sample_recipients.length})
          </div>
          <div className="rounded-lg border border-gray-700 bg-gray-900/40 divide-y divide-gray-800" data-testid="campaign-sample-recipients">
            {sample_recipients.map((r, i) => (
              <div key={`${r.user_id || 'r'}-${i}`} className="px-3 py-2 text-xs flex items-center justify-between">
                <span className="text-gray-200">{r.name || <span className="text-gray-500 italic">(no name)</span>}</span>
                <span className="text-gray-400 break-all ml-2">{r.email}</span>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-gray-500 mt-1 flex items-center gap-1">
            <Shield className="w-2.5 h-2.5" /> Non-Owner roles see masked PII.
          </div>
        </div>
      )}

      <div>
        <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">Send from</label>
        <select
          value={senderPersona}
          onChange={(e) => setSenderPersona(e.target.value)}
          className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-hidden focus:ring-2 focus:ring-green-500"
          data-testid="campaign-sender-select"
        >
          {SENDER_PERSONAS.map((p) => (
            <option key={p.key} value={p.key}>{p.label}</option>
          ))}
        </select>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-300 hover:text-white" data-testid="campaign-preview-cancel">Cancel</button>
        <button
          onClick={onContinue}
          disabled={isEmpty}
          className="px-4 py-2 bg-green-600 hover:bg-green-500 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg text-sm font-medium transition flex items-center gap-1.5"
          data-testid="campaign-preview-continue"
        >
          Continue to write the email
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Step 2: Composer
// ─────────────────────────────────────────────
export function ComposeStep({ subject, setSubject, body, setBody, sample, onBack, onContinue, onSendTest, sendingTest }) {
  const textareaRef = useRef(null);

  const insertTag = (tag) => {
    const el = textareaRef.current;
    if (!el) {
      setBody((prev) => prev + tag);
      return;
    }
    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    const newBody = body.slice(0, start) + tag + body.slice(end);
    setBody(newBody);
    setTimeout(() => {
      el.focus();
      const next = start + tag.length;
      el.setSelectionRange(next, next);
    }, 0);
  };

  const previewCtx = sample || { name: 'Sade Adeyemi', country: 'Nigeria' };
  const firstName = (previewCtx.name || '').split(' ')[0] || 'there';
  const previewBody = (body || '')
    .replace(/\{\{first_name\}\}/g, firstName)
    .replace(/\{\{full_name\}\}/g, previewCtx.name || 'there')
    .replace(/\{\{country\}\}/g, previewCtx.country || 'your country');

  return (
    <div className="space-y-4" data-testid="campaign-compose-step">
      <div>
        <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">Subject</label>
        <input
          type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
          placeholder="e.g. We miss you — here's what's new" maxLength={300}
          className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-hidden focus:ring-2 focus:ring-green-500"
          data-testid="campaign-subject-input"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs uppercase tracking-wide text-gray-400">Message (plain text)</label>
            <div className="flex flex-wrap gap-1">
              {MERGE_TAGS.map((t) => (
                <button
                  key={t.tag} onClick={() => insertTag(t.tag)}
                  className="px-1.5 py-0.5 text-[10px] rounded bg-green-500/10 text-green-300 border border-green-500/30 hover:bg-green-500/20"
                  data-testid={`campaign-merge-${t.label.toLowerCase().replace(/\s/g, '-')}`}
                  title={`Inserts ${t.tag} — sample value: ${t.sample}`}
                >
                  + {t.label}
                </button>
              ))}
            </div>
          </div>
          <textarea
            ref={textareaRef} value={body} onChange={(e) => setBody(e.target.value)}
            placeholder={"Hi {{first_name}},\n\nWe noticed you haven't logged in lately. Here's what's new..."}
            rows={10} maxLength={20000}
            className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm font-mono focus:outline-hidden focus:ring-2 focus:ring-green-500 resize-y"
            data-testid="campaign-body-input"
          />
          <div className="text-[10px] text-gray-500 mt-1">
            {body.length}/20,000 characters · The unsubscribe footer is added automatically (legally required)
          </div>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">Live preview (using sample user)</label>
          <div className="rounded-lg border border-gray-700 bg-white p-4 text-gray-900 text-sm whitespace-pre-wrap leading-relaxed min-h-64 max-h-80 overflow-y-auto" data-testid="campaign-live-preview">
            <div className="mb-3 pb-2 border-b border-gray-200">
              <div className="text-xs text-gray-500">Subject:</div>
              <div className="font-semibold">{subject || <span className="italic text-gray-400">(empty subject)</span>}</div>
            </div>
            {previewBody || <span className="italic text-gray-400">Start typing on the left to see the preview…</span>}
            <div className="mt-4 pt-3 border-t border-gray-200 text-xs text-gray-500 leading-snug">
              You're receiving this because you have an account at MyInvestFolio.<br />
              To stop receiving follow-ups like this, <span className="underline">unsubscribe here</span>.<br />
              MyInvestFolio<br />
              No. 8 Rasheed Alaba Williams Street, Lekki Phase 1, Lagos, Nigeria
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2 gap-2 flex-wrap">
        <button onClick={onBack} className="px-4 py-2 text-sm text-gray-300 hover:text-white" data-testid="campaign-compose-back">Back</button>
        <div className="flex items-center gap-2">
          <button
            onClick={onSendTest}
            disabled={sendingTest || !subject.trim() || !body.trim()}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600 text-white rounded-lg text-sm font-medium transition flex items-center gap-1.5"
            data-testid="campaign-send-test"
          >
            {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Send test to me
          </button>
          <button
            onClick={onContinue}
            disabled={!subject.trim() || !body.trim()}
            className="px-4 py-2 bg-green-600 hover:bg-green-500 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg text-sm font-medium transition flex items-center gap-1.5"
            data-testid="campaign-compose-continue"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Step 3: Confirm + Send
// ─────────────────────────────────────────────
export function ConfirmStep({ subject, audienceCount, senderPersonaLabel, onBack, onSend, sending, confirmText, setConfirmText }) {
  const matches = confirmText.trim().toUpperCase() === 'SEND';
  return (
    <div className="space-y-4" data-testid="campaign-confirm-step">
      <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          You're about to email <strong className="text-amber-100">{audienceCount.toLocaleString()} {audienceCount === 1 ? 'user' : 'users'}</strong> from <strong className="text-amber-100">{senderPersonaLabel}</strong>.
          <br />Subject: <em className="text-amber-100">{subject}</em>
          <br /><span className="text-amber-300">This cannot be undone once sending starts.</span>
        </div>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">Type SEND to confirm</label>
        <input
          type="text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
          placeholder="SEND" autoFocus
          className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm font-mono uppercase focus:outline-hidden focus:ring-2 focus:ring-amber-500"
          data-testid="campaign-confirm-input"
        />
      </div>
      <div className="flex items-center justify-end gap-2 pt-2">
        <button onClick={onBack} disabled={sending} className="px-4 py-2 text-sm text-gray-300 hover:text-white disabled:opacity-50" data-testid="campaign-confirm-back">Back</button>
        <button
          onClick={onSend}
          disabled={!matches || sending}
          className="px-4 py-2 bg-red-600 hover:bg-red-500 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg text-sm font-medium transition flex items-center gap-1.5"
          data-testid="campaign-confirm-send"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send to {audienceCount.toLocaleString()} {audienceCount === 1 ? 'user' : 'users'}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Step 4: Done
// ─────────────────────────────────────────────
export function DoneStep({ campaignId, audienceCount, onClose }) {
  return (
    <div className="text-center py-6 space-y-3" data-testid="campaign-done-step">
      <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto" />
      <div className="text-lg font-semibold text-white">Campaign queued</div>
      <div className="text-sm text-gray-400">
        {audienceCount.toLocaleString()} {audienceCount === 1 ? 'email is' : 'emails are'} in the send queue.<br />
        Delivery happens in the background respecting the Resend daily cap.
      </div>
      <div className="text-xs text-gray-500 font-mono">{campaignId}</div>
      <button onClick={onClose} className="mt-3 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium" data-testid="campaign-done-close">Close</button>
    </div>
  );
}

// ─────────────────────────────────────────────
// Modal container
// ─────────────────────────────────────────────
export function ModalShell({ onClose, children, title }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-xs flex items-center justify-center p-3"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="campaign-modal-overlay"
    >
      <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="campaign-modal">
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-4 py-3 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-green-400" />
            <h3 className="text-base font-semibold text-white">{title}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="campaign-modal-close" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
